import { Input, Tooltip } from '@design-systems/canvas-ui'
import Icon, { InfoIcon } from '@design-systems/react-icons'
import { CustomTooltip, isOthersSelected } from 'mvscommoncomponents'
import { getProductHierarchyUnitValue, getUniqueKeyName } from '../../common'
import { TruncatedValueTooltip } from '../../common/TruncatedValueTooltip'
import {
  CONDITION_DEFAULT,
  CONTROL_PHONE_NUMBER_FIELD_PHONE,
  DEFAULT_WIDTH_PERCENT,
  INTERNAL_TESTING_METHODS_SECTION_UID,
  TRANSITION_TIME_PIXEL,
} from '../../common/commonConstants'

/**
 * Utility to generate Label, Info icon with message, Mandatory indicator (for any control)
 * @param {*} control
 * @returns
 */
export const renderControlLabel = (control) => {
  return control.label_name ? (
    <div className="row-centre">
      <Input.Label
        key={getUniqueKeyName(control.label_name)}
        id={getUniqueKeyName(control.label_name)}
        className="hc8-fs-100 hc8-mb-none text-scroll-container font-weight-400"
        onMouseEnter={(event) => {
          const container = event.currentTarget
          const textElement = container?.lastElementChild

          if (container && textElement) {
            const containerWidth = parseFloat(getComputedStyle(container).width)
            const textWidth = textElement.clientWidth
            const movementValue = Math.min(containerWidth - textWidth, 0)
            const duration = Math.max(
              -TRANSITION_TIME_PIXEL * movementValue * 1000,
              0,
            )

            textElement
              .getAnimations()
              .forEach((animation) => animation.cancel())
            textElement.animate(
              [
                { transform: 'translateX(0)' },
                { transform: `translateX(${movementValue}px)` },
              ],
              {
                duration,
                easing: 'linear',
                fill: 'forwards',
              },
            )
          }
        }}
        onMouseLeave={(event) => {
          const textElement = event.currentTarget?.lastElementChild

          if (textElement) {
            textElement
              .getAnimations()
              .forEach((animation) => animation.cancel())
            textElement.animate(
              [{ transform: 'translateX(0)' }, { transform: 'translateX(0)' }],
              { duration: 0, fill: 'forwards' },
            )
          }
        }}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: [control.label_name],
          }}
        ></span>
        {control.is_mandatory && (
          <span className="hc8-pl-2x">
            <strong>*</strong>
          </span>
        )}
      </Input.Label>

      {control.info_icon && (
        <Tooltip
          className="hc8-ml-2x"
          content={control.info_icon}
          location="right"
        >
          <Icon className="row-centre" svg={InfoIcon} />
        </Tooltip>
      )}
    </div>
  ) : null
}

/**
 * Builds baner text - Replaces placeholders in the bannerMasterText with corresponding values from dataNode.
 *
 * @param {string} bannerMasterText - The text containing placeholders to be replaced.
 * @param {Object} dataNode - An object where keys are placeholders in the bannerMasterText and values are the replacements.
 * @returns {string} - The bannerMasterText with placeholders replaced by corresponding values from dataNode.
 */
export const buildBannerText = (bannerMasterText, dataNode) => {
  return dataNode
    ? Object.keys(dataNode).reduce(
        (text, key) => text.replace(`${key}`, dataNode[key]),
        bannerMasterText,
      )
    : ''
}

/**
 * Render small control header text
 * @param {*} control
 * @returns
 */
export const renderControlLabelSmallHeaderOnly = (control) => {
  return control.label_name ? (
    <div className="row-centre">
      <Input.Label
        className="hc8-fs-050 hc8-mb-none hc8-clr-subdued"
        key={getUniqueKeyName(control.label_name)}
        id={getUniqueKeyName(control.label_name)}
      >
        <p
          className="hc8-clr-disabled"
          dangerouslySetInnerHTML={{
            __html: [control.label_name],
          }}
        ></p>
      </Input.Label>
      {control.info_icon && (
        <CustomTooltip
          key={`tooltip`}
          placement="right"
          title={control.info_icon}
          arrow
          component={
            <Icon
              className="row-centre hc8-pl-2x hc8-clr-brand"
              svg={InfoIcon}
            />
          }
        />
      )}
    </div>
  ) : null
}

/**
 * Formats a given number string into the US number format.
 * @param {string} numberString - The number string to be formatted.
 * @returns {string} - The formatted number string in US number format, or the original string if it is not a number.
 */
export const getUsNumberFormat = (numberString) => {
  return !numberString || isNaN(numberString)
    ? numberString
    : new Intl.NumberFormat('en-US').format(numberString)
}

/**
 * Renders a read-only header label with a value and optional unit display.
 * @param {string} valueToDisplay - The value to be displayed in the header.
 * @param {Object} control - The control object containing metadata for the label.
 * @param {Object} sectionData - The section data related to the control.
 * @param {Object} rfiMasterData - The master data related to the RFI (Request for Information).
 * @returns {JSX.Element} A JSX element containing the read-only header label and value.
 */
export const renderLabelReadOnlyHeader = (
  valueToDisplay,
  control,
  sectionData,
  rfiMasterData,
) => {
  const isMultiValue =
    control.metadata?.sectionUid === INTERNAL_TESTING_METHODS_SECTION_UID &&
    Array.isArray(valueToDisplay)

  return (
    <div className="width-100 control-helper__readonly">
      <Input.Label
        className="hc8-fs-050 hc8-mb-none hc8-clr-subdued control-helper__readonly-label"
        key={`label-header-${control.uid}`}
        id={`label-header-${control.uid}`}
        data-testid="label-read-only-header"
      >
        <p
          className="hc8-clr-disabled"
          dangerouslySetInnerHTML={{
            __html: [control.label_name],
          }}
        ></p>
      </Input.Label>
      {isMultiValue ? (
        <div className="lcm-readonly-chip-list">
          {valueToDisplay.map((value, index) => {
            const label = value?.label ?? value?.value ?? value?.id ?? value

            return (
              <div
                className="lcm-readonly-chip-list__chip"
                key={`${control.uid}-${label}-${index}`}
              >
                <TruncatedValueTooltip
                  targetSelector=".lcm-readonly-chip-list__value"
                  value={label}
                >
                  <Input.Chip
                    className="lcm-readonly-chip-list__value"
                    size="sm"
                    label={label}
                    isDisabled
                  />
                </TruncatedValueTooltip>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="align-centre-left control-helper__readonly-value">
          <Input.Label
            className="hc8-fs-100 hc8-ma-none font-weight-400 control-helper__readonly-value-text"
            key={`label-header-value-${control.uid}`}
            id={`label-header-value-${control.uid}`}
          >
            <p
              className="control-helper__readonly-value-paragraph"
              dangerouslySetInnerHTML={{
                __html: [valueToDisplay],
              }}
            ></p>
          </Input.Label>
          <p>{embedUnitDisplay(control, sectionData, rfiMasterData)}</p>
        </div>
      )}
    </div>
  )
}

/**
 * Get the control's personal width
 * 1. If width in attribute - take width
 * 2. If not in attribute - take default width
 * 3. If unit field present - Shrink width by 30%
 * @param {*} control
 * @returns
 */
export const getControlWidth = (control) => {
  //If unit field - shrink to 70% (space for unit display)
  const unitFieldAdjustment = control.display_unit ? 0.7 : 1

  const calcWidth =
    unitFieldAdjustment *
    (control.width_percentage
      ? Number(control.width_percentage)
      : DEFAULT_WIDTH_PERCENT)

  return `${calcWidth}%`
}

/**
 * Get height class css
 * @param {*} heightSetting
 * @returns
 */
export const getHeightClass = (heightSetting) => {
  switch (heightSetting?.toLowerCase()) {
    case 'none':
      return 'hc8-mt-none'
    case 'dense':
      return 'hc8-mt-2x'
    case 'normal':
      return 'hc8-mt-3x'
    case 'expanded':
      return 'hc8-mt-6x'
    default:
      return 'hc8-mt-2x'
  }
}

/**
 * Get the unit display field (if control attribute has it)
 * NOTE:
 * If => control.display_unit.field is available evaluate the units
 *  a. From master data if available
 *  b. from condition
 * Else => check if unit_value is already available in the template and display it
 * @param {*} control
 * @param {*} sectionData
 * @param {*} rfiMasterData
 * @returns
 */
export const embedUnitDisplay = (
  control,
  sectionData,
  rfiMasterData = null,
) => {
  if (control.display_unit) {
    let unitValue = ''
    //Note: Fetch units in this order
    //1. Unit value from master data (hierarchy - field uid matches with hierarchy level uid property)
    //2. Unit value from template
    if (control.display_unit.field) {
      const foundUnitValueFromMaster = getProductHierarchyUnitValue(
        rfiMasterData,
        sectionData,
        control.display_unit.field,
        control.uid,
      )
      if (foundUnitValueFromMaster) {
        unitValue = foundUnitValueFromMaster
      } else {
        const sourceFieldValue = sectionData[control.display_unit.field]
        unitValue = control.display_unit.condition?.[sourceFieldValue]
        unitValue = unitValue
          ? unitValue
          : control.display_unit.condition?.[CONDITION_DEFAULT]
      }
    } else {
      unitValue = control.metadata?.unit_value
        ? control.metadata?.unit_value
        : control.display_unit.condition?.[CONDITION_DEFAULT]
    }
    return <p className="hc8-pl-2x">{unitValue}</p>
  }
}

/**
 * Handle Other value display - If others is selected append other value to it
 * @param {*} value
 * @param {*} otherValue
 * @returns
 */
export const handleOtherValueDisplay = (value, otherValue) => {
  return isOthersSelected(value)
    ? `${value} - ${otherValue ? otherValue : ''}`
    : value
}

/**
 * Display error message
 * @param {*} errorMessage
 * @returns
 */
export const showErrorMessage = (errorMessage) => {
  return (
    <Input.Label className="error-color font-weight-400" isErrored>
      {errorMessage}
    </Input.Label>
  )
}

/**
 * Render textbox to show "Others" for any control that allows others value
 * @param {*} control
 * @param {*} sectionData
 * @param {*} onInputChangeHandler
 * @param {*} otherErrorMessage
 * @returns
 */
export const captureOthersValue = (
  control,
  sectionData,
  onInputChangeHandler,
  otherErrorMessage,
) => {
  if (isOthersSelected(sectionData[control.uid])) {
    return renderCaptureOthersValue(
      control,
      sectionData,
      onInputChangeHandler,
      otherErrorMessage,
    )
  }
}

export const renderCaptureOthersValue = (
  control,
  sectionData,
  onInputChangeHandler,
  otherErrorMessage,
) => {
  return (
    <>
      <Input.Text
        key={`autocomplete-input-other-${control.uid}`}
        id={`autocomplete-input-other-${control.uid}`}
        className="hc8-mt-2x"
        value={sectionData[`other_${control.uid}`] ?? ''}
        isErrored={Boolean(otherErrorMessage)}
        placeholder={control.placeholder_others}
        onChange={(event) => {
          const newValue = event.target.value
          const updateMetadata = {
            uid: `other_${control.uid}`,
            inputMetadata: control.metadata,
          }
          onInputChangeHandler(newValue, updateMetadata)
        }}
      />
      {otherErrorMessage && (
        <div className="hc8-pt-2x">{showErrorMessage(otherErrorMessage)}</div>
      )}
    </>
  )
}

//--------Phone number input handling--------

/**
 * Extract phone number prop names
 * Handles normal phone number / fax etc
 * Figures out the phone number type (normal phone / fax etc) & tells the right model class to which the country code & number is to be mapped
 * @param {*} controlMetadata
 * @returns
 */
export const extractPhoneNumberProps = (controlMetadata) => {
  const countryCodeProp = `${
    !controlMetadata.numberType ||
    controlMetadata.numberType.toLowerCase() ===
      CONTROL_PHONE_NUMBER_FIELD_PHONE
      ? ''
      : controlMetadata.numberType + '_'
  }country_code`
  const phoneNumberProp = `${
    !controlMetadata.numberType ||
    controlMetadata.numberType.toLowerCase() ===
      CONTROL_PHONE_NUMBER_FIELD_PHONE
      ? ''
      : controlMetadata.numberType + '_'
  }phone_number`
  return {
    countryCodeProp,
    phoneNumberProp,
  }
}
